"use client";

import { TypeGridItem, TypeGridItemCollection } from "@contentful/types";
import {
  faArrowRight,
  faAward,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { notEmpty } from "@yardzen/js-util";
import { customImageLoader } from "@yardzen/next-client-util";
import { Avatar, AvatarImage } from "@yardzen/ui/components/avatar";
import { Button } from "@yardzen/ui/components/button";
import { Card, CardContent } from "@yardzen/ui/components/card";
import {
  ContentCard,
  contentCardActionClassName,
} from "@yardzen/ui/components/content-card";
import {
  Carousel,
  CarouselContent,
  CarouselDots,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@yardzen/ui/components/carousel";
import { ContentfulText } from "@yardzen/ui/components/contentful-text";
import { H3 } from "@yardzen/ui/components/h3";
import { H5 } from "@yardzen/ui/components/h5";
import { Icon, isIconName } from "@yardzen/ui/components/icon";
import { Link } from "@yardzen/ui/components/link";
import { P } from "@yardzen/ui/components/p";
import { cn } from "@yardzen/ui/lib/utils";
import { cva } from "class-variance-authority";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { StarRating } from "./StarRating";

export function GridItemCollection({
  gridItemCollection,
}: {
  gridItemCollection: TypeGridItemCollection<"WITHOUT_UNRESOLVABLE_LINKS">;
}) {
  const router = useRouter();

  const {
    heading,
    subheading,
    description,
    cta,
    backgroundColor,
    width,
    navigationOptions,
    collectionImage,
    gridItems,
    itemImageStyle,
    imageAlignment,
    numberOfDesktopColumns,
    numberOfMobileColumns,
  } = gridItemCollection.fields ?? {};

  const variantResolved = itemImageStyle ?? "large-top-image";
  /**
   * CSS grid cannot centre a row that does not fill its columns, so this style
   * lays out with flex-wrap and an explicit per-card width instead — a
   * trailing row of leftovers sits in the middle rather than against the edge.
   */
  const centersTrailingRow = variantResolved === "medium-top-image";
  const backgroundVariantResolved = backgroundColor ?? "none";
  const containerWidthResolved = width ?? "75%";
  const imageAlignmentResolved = imageAlignment ?? "top";
  const hasSlideshow = (navigationOptions?.length ?? 0) > 0;
  const hasHeaderBlock = Boolean(heading || subheading || description);
  const avatar =
    collectionImage?.fields.avatar?.fields.file?.url ||
    collectionImage?.fields.image?.fields.file?.url;
  const avatarAlt =
    collectionImage?.fields.label || collectionImage?.fields.description;
  const desktopColumnsResolved = Number(numberOfDesktopColumns || 3) as
    | 1
    | 2
    | 3
    | 4
    | 5
    | 6;
  const mobileColumnsResolved = numberOfMobileColumns || 1;

  const showDots = navigationOptions?.includes("bottom-dots");
  const showArrows = navigationOptions?.includes("left-right-arrows");
  const gridVariants = cva("", {
    variants: {
      wrapperClass: {
        "large-top-image": "",
        "small-top-image": "gap-4",
        "medium-top-image": "gap-6",
        headshot: "gap-x-4 gap-y-8 max-w-[760px] mx-auto",
        "small-left-side-image": "",
        "medium-left-side-image": "",
        na: "",
      },
      desktopColumns: {
        1: "md:grid-cols-1 md:basis-full",
        2: "md:grid-cols-2 md:basis-1/2",
        3: "md:grid-cols-3 md:basis-1/3",
        4: "md:grid-cols-4 md:basis-1/4",
        5: "md:grid-cols-5 md:basis-1/5",
        6: "md:grid-cols-6 md:basis-1/6",
      },
      mobileColumns: {
        1: "max-md:grid-cols-1 max-md:basis-full",
        2: "max-md:grid-cols-2 max-md:basis-1/2",
      },
      /**
       * Widths for the flex-wrap layout above. The math subtracts the `gap-6`
       * (1.5rem) `medium-top-image` lays out with — move one, move the other.
       */
      centeredDesktopColumns: {
        1: "md:basis-auto",
        2: "md:basis-[calc((100%_-_1.5rem)/2)]",
        3: "md:basis-[calc((100%_-_2*1.5rem)/3)]",
        4: "md:basis-[calc((100%_-_3*1.5rem)/4)]",
        5: "md:basis-[calc((100%_-_4*1.5rem)/5)]",
        6: "md:basis-[calc((100%_-_5*1.5rem)/6)]",
      },
      centeredMobileColumns: {
        1: "basis-full sm:basis-[calc((100%_-_1.5rem)/2)]",
        2: "basis-[calc((100%_-_1.5rem)/2)]",
      },
      alignColumns: {
        top: "",
        middle: "items-center",
      },
      backgroundColor: {
        "light-green": "mt-6 bg-brand-beige-lighter",
        white: "mt-6 bg-white",
        "sky-light": "mt-6 bg-brand-sky-light",
        none: "",
      },
      containerWidth: {
        "100%": "",
        "75%": "max-w-7xl",
      },
    },
  });

  return (
    <section
      className={cn(
        "w-full py-16",
        containerWidthResolved === "100%" ? "px-4 md:px-0" : "px-8",
        gridVariants({ backgroundColor: backgroundVariantResolved }),
      )}
    >
      <div
        className={cn(
          "mx-auto",
          gridVariants({ containerWidth: containerWidthResolved }),
        )}
      >
        {avatar && (
          <Avatar>
            <AvatarImage
              src={avatar}
              alt={avatarAlt}
              className="mx-auto mb-2 h-[48px] w-[48px] object-cover object-top"
            />
          </Avatar>
        )}
        {hasHeaderBlock && (
          <div className="mb-6 text-center">
            {heading && (
              <H3 className="font-display text-brand-secondary-dark pb-6 text-5xl">
                {heading}
              </H3>
            )}

            {subheading && (
              <P
                noSpacing
                className="text-brand-moss-greens-90 mx-auto mb-6 w-[90%] text-lg leading-[140%] md:max-w-[65%]"
              >
                {subheading}
              </P>
            )}

            {description && (
              <ContentfulText
                className="mx-auto md:max-w-[65%]"
                content={description}
              />
            )}
          </div>
        )}

        {hasSlideshow && gridItems ? (
          <Carousel
            className="h-full w-full md:px-6"
            opts={{
              loop: true,
            }}
          >
            <CarouselContent>
              {gridItems &&
                gridItems.filter(notEmpty).map((gridItem, i) => (
                  <CarouselItem
                    key={`gridItem-${i}-${gridItem.sys.id}`}
                    className={cn(
                      gridVariants({
                        desktopColumns: desktopColumnsResolved,
                        mobileColumns: mobileColumnsResolved,
                      }),
                    )}
                  >
                    <GridItemCard
                      gridItem={gridItem}
                      gridItemCollection={gridItemCollection}
                      centerButton={true}
                    />
                  </CarouselItem>
                ))}
            </CarouselContent>
            {showDots && gridItems && (
              <CarouselDots count={gridItems.length} className="mt-2" />
            )}

            {showArrows && (
              <>
                <CarouselPrevious
                  className={cn("bg-brand-greens-light left-0")}
                  aria-label="Previous"
                  variant={"zest"}
                  icon={faChevronLeft}
                />
                <CarouselNext
                  className={cn("bg-brand-greens-light right-0")}
                  aria-label="Next"
                  variant={"zest"}
                  icon={faChevronRight}
                />
              </>
            )}
          </Carousel>
        ) : (
          <div
            className={cn(
              "justify-center gap-8",
              centersTrailingRow
                ? "flex flex-wrap"
                : cn(
                    "grid",
                    `grid-cols-${mobileColumnsResolved} md:grid-cols-${desktopColumnsResolved}`,
                    ["large-top-image", "small-top-image"].includes(
                      variantResolved,
                    )
                      ? "sm:grid-cols-2"
                      : "",
                  ),
              gridVariants({
                wrapperClass: variantResolved,
                alignColumns: imageAlignmentResolved,
              }),
            )}
            style={{
              justifyItems:
                !centersTrailingRow && desktopColumnsResolved === 1
                  ? "center"
                  : undefined,
            }}
          >
            {gridItems &&
              gridItems.filter(notEmpty).map((gridItem, i) => (
                <GridItemCard
                  key={`gridItem-${i}-${gridItem.sys.id}`}
                  gridItem={gridItem}
                  gridItemCollection={gridItemCollection}
                  centerButton={false}
                  isSingleColumn={desktopColumnsResolved === 1}
                  className={
                    centersTrailingRow
                      ? gridVariants({
                          centeredDesktopColumns: desktopColumnsResolved,
                          centeredMobileColumns: mobileColumnsResolved,
                        })
                      : undefined
                  }
                />
              ))}
          </div>
        )}
        {cta && (
          <div className="mt-6 text-center">
            <Button
              className="w-full px-4 py-3 md:w-auto"
              variant={cta.fields.variant}
              endIcon={<FontAwesomeIcon icon={faArrowRight} />}
              {...(cta.fields.openInNewTab && cta.fields.url
                ? { asChild: true }
                : {
                    onClick: () => {
                      cta.fields.url && router.push(cta.fields.url);
                    },
                  })}
            >
              {cta.fields.openInNewTab && cta.fields.url ? (
                <a
                  href={cta.fields.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {cta.fields.buttonText}
                </a>
              ) : (
                cta.fields.buttonText
              )}
            </Button>
          </div>
        )}
      </div>
    </section>
  );

  function GridItemCard({
    gridItem,
    gridItemCollection,
    centerButton,
    isSingleColumn,
    className,
  }: {
    gridItem: TypeGridItem<"WITHOUT_UNRESOLVABLE_LINKS">;
    gridItemCollection: TypeGridItemCollection<"WITHOUT_UNRESOLVABLE_LINKS">;
    centerButton: boolean;
    isSingleColumn?: boolean;
    /** Extra classes for the card itself — the centered layout's width. */
    className?: string;
  }) {
    const variantResolved =
      gridItemCollection.fields?.itemImageStyle ?? "large-top-image";

    // Image-only items (logo strips) aren't cards: they keep the plain image rendering below.
    const hasCardContent = Boolean(
      gridItem.fields.heading ||
        gridItem.fields.textBlock ||
        gridItem.fields.cta,
    );

    if (variantResolved === "large-top-image" && hasCardContent) {
      return (
        <MediaTopGridItemCard
          gridItem={gridItem}
          className={cn(isSingleColumn && "md:mx-auto md:w-fit", className)}
        />
      );
    }

    const hasHeadingInner = Boolean(
      gridItem.fields?.heading ||
        gridItem.fields?.starRating ||
        gridItem.fields?.subheading ||
        gridItem.fields?.tertiaryHeading,
    );
    const hasHeadingOuter = Boolean(
      gridItem.fields?.textBlock ||
        gridItem.fields?.badge === "super-pro" ||
        hasHeadingInner,
    );
    const imageSize =
      variantResolved === "large-top-image"
        ? 750
        : variantResolved === "medium-top-image"
          ? 120
          : variantResolved === "medium-left-side-image"
            ? 100
            : 70;
    const ctaUrl =
      gridItem.fields.cta?.fields?.url ?? gridItem.fields?.image?.fields?.link;
    const gridVariants = cva("", {
      variants: {
        itemBackgroundColor: {
          "light-grey": "bg-[#626E581A] p-6",
          "light-green": "bg-brand-beige-lighter p-6",
          white: "bg-white p-6",
          none: "bg-inherit",
        },
        itemClass: {
          "large-top-image": "flex flex-col",
          "small-top-image": "",
          "medium-top-image":
            "bg-[#F5F3EF] p-6 rounded-xl min-h-[160px] flex items-center",
          headshot: "",
          "small-left-side-image": "pl-4 shrink",
          "medium-left-side-image": "p-4 shrink",
          na: "",
        },
        itemCardClass: {
          "large-top-image": "flex flex-1 flex-col",
          "small-top-image": "",
          "medium-top-image":
            "flex flex-col items-center justify-center w-full",
          headshot: "px-2 pb-4",
          "small-left-side-image": "flex",
          "medium-left-side-image": "flex",
          na: "",
        },
        itemImageWrapperClass: {
          "large-top-image": "w-full",
          "small-top-image": "w-full",
          "medium-top-image": "w-full flex items-center justify-center",
          headshot: "w-full",
          "small-left-side-image": "flex-none w-[52px] mr-3",
          "medium-left-side-image": "flex-none mr-3",
          na: "w-full",
        },
        itemImageClass: {
          "large-top-image": "h-full w-full object-cover",
          "small-top-image": "h-[70px] w-[70px] m-auto",
          "medium-top-image":
            "w-auto max-w-[130px] h-auto m-auto object-contain",
          headshot: "h-[100px] w-[100px] m-auto rounded-full",
          "small-left-side-image": "rounded-lg h-[50px] w-[50px]",
          "medium-left-side-image": "rounded-lg h-[100px] w-[100px]",
          na: "h-full w-full",
        },
        itemWrapperClass: {
          "large-top-image": "pt-6",
          "small-top-image": "text-center pt-4",
          "medium-top-image": "text-center pt-1",
          headshot: "text-center pt-2",
          "small-left-side-image": "py-0",
          "medium-left-side-image": "py-0",
          na: "text-center",
        },
        itemHeadingClass: {
          "large-top-image": "text-2xl",
          "small-top-image": "",
          "medium-top-image": "text-lg",
          headshot: "text-lg",
          "small-left-side-image": "text-2xl",
          "medium-left-side-image": "text-xl",
          na: "text-2xl",
        },
        itemTextClass: {
          "large-top-image": "text-lg leading-[140%]",
          "small-top-image": "text-sm leading-[140%] text-brand-moss-greens-90",
          "medium-top-image":
            "text-base leading-[140%] text-brand-moss-greens-90",
          headshot: "text-base leading-[140%] text-brand-moss-greens-90",
          "small-left-side-image":
            "text-sm leading-[140%] text-brand-moss-greens-90",
          "medium-left-side-image":
            "leading-[130%] text-brand-moss-greens-90 text-xxs",
          na: "text-lg leading-[140%]",
        },
      },
    });

    return (
      <Card
        className={cn(
          "overflow-hidden border-none shadow-none max-md:mx-auto",
          isSingleColumn && "md:mx-auto md:w-fit",
          gridVariants({
            itemBackgroundColor: gridItem.fields.backgroundColor || "none",
            itemClass: variantResolved,
          }),
          className,
        )}
      >
        <CardContent
          className={cn(
            "justify-start p-0",
            gridVariants({
              itemCardClass: variantResolved,
            }),
          )}
        >
          {(gridItem.fields?.image?.fields?.image?.fields.file?.url ||
            (gridItem?.fields.icon && isIconName(gridItem?.fields.icon))) && (
            <div
              className={cn(
                "overflow-hidden text-center",
                gridVariants({ itemImageWrapperClass: variantResolved }),
              )}
            >
              <Link
                variant="nav"
                className={ctaUrl ? "cursor-pointer" : ""}
                onClick={() => {
                  const url = ctaUrl;
                  if (!url) return;
                  if (
                    gridItem.fields.cta?.fields?.openInNewTab === true ||
                    gridItem.fields?.image?.fields?.link
                  ) {
                    window.open(url, "_blank");
                  } else {
                    router.push(url);
                  }
                }}
              >
                {gridItem.fields?.image?.fields?.image?.fields.file?.url && (
                  <Image
                    src={
                      gridItem.fields?.image?.fields?.image?.fields.file?.url
                    }
                    alt={gridItem.fields?.image?.fields.description ?? ""}
                    className={cn(
                      gridVariants({ itemImageClass: variantResolved }),
                    )}
                    height={imageSize}
                    width={imageSize}
                    loading="lazy"
                    loader={customImageLoader}
                  />
                )}
                {gridItem?.fields.icon && isIconName(gridItem?.fields.icon) && (
                  <div className="flex items-center justify-center">
                    <div className="bg-system-grayscale-10 flex rounded-full p-3 shadow-sm">
                      <Icon
                        name={gridItem?.fields.icon}
                        size="sm"
                        className="h-7 w-7"
                      />
                    </div>
                  </div>
                )}
              </Link>
            </div>
          )}
          <div className="flex flex-1 flex-col">
            {hasHeadingOuter && (
              <div
                className={cn(
                  "justify-start",
                  gridVariants({
                    itemWrapperClass: variantResolved,
                  }),
                )}
              >
                {hasHeadingInner && (
                  <div className="mb-1">
                    <H5
                      className={cn(
                        "font-display text-scheme-text-primary pb-1 text-xl",
                        gridVariants({
                          itemHeadingClass: variantResolved,
                        }),
                      )}
                    >
                      {gridItem.fields?.heading}
                    </H5>
                    {gridItem.fields?.starRating && (
                      <StarRating
                        starRating={gridItem.fields.starRating}
                        variant="left-aligned-small"
                      />
                    )}
                    {gridItem.fields?.subheading && (
                      <P
                        className={cn(
                          "text-scheme-text-secondary mb-0",
                          gridVariants({
                            itemTextClass: variantResolved,
                          }),
                        )}
                        noSpacing
                      >
                        {gridItem.fields?.subheading}
                      </P>
                    )}
                    {gridItem.fields?.tertiaryHeading && (
                      <P
                        className={cn(
                          "text-scheme-text-secondary mb-0 mt-2",
                          gridVariants({
                            itemTextClass: variantResolved,
                          }),
                        )}
                        noSpacing
                      >
                        {gridItem.fields?.tertiaryHeading}
                      </P>
                    )}
                  </div>
                )}
                {gridItem.fields?.textBlock && (
                  <ContentfulText
                    content={gridItem.fields?.textBlock}
                    className="mt-1"
                    pClassName={cn(
                      "text-scheme-text-secondary mb-0",
                      gridVariants({
                        itemTextClass: variantResolved,
                      }),
                    )}
                  />
                )}
                {gridItem.fields?.badge === "super-pro" && (
                  <div className="mt-2 inline-block rounded-full bg-[#ECF4BF] px-2 py-1 text-xs font-semibold">
                    <FontAwesomeIcon icon={faAward} />
                    <span className="pl-2">Super Pro</span>
                  </div>
                )}
              </div>
            )}
            {gridItem.fields?.cta && (
              <div
                className={cn("mt-auto pt-4", centerButton && "text-center")}
              >
                <Button
                  className="w-full sm:w-auto"
                  variant={gridItem.fields?.cta.fields.variant}
                  endIcon={<FontAwesomeIcon icon={faArrowRight} />}
                  {...(gridItem.fields.cta.fields.openInNewTab &&
                  gridItem.fields.cta.fields.url
                    ? { asChild: true }
                    : {
                        onClick: () => {
                          gridItem.fields?.cta?.fields.url &&
                            router.push(gridItem.fields.cta.fields.url);
                        },
                      })}
                >
                  {gridItem.fields.cta.fields.openInNewTab &&
                  gridItem.fields.cta.fields.url ? (
                    <a
                      href={gridItem.fields.cta.fields.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {gridItem.fields.cta.fields.buttonText}
                    </a>
                  ) : (
                    gridItem.fields?.cta.fields.buttonText
                  )}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }
}

/** A large-top-image grid item: edge-to-edge photo, title, text, text-only action. */
function MediaTopGridItemCard({
  gridItem,
  className,
}: {
  gridItem: TypeGridItem<"WITHOUT_UNRESOLVABLE_LINKS">;
  className?: string;
}) {
  const { heading, textBlock, cta, image, backgroundColor } = gridItem.fields;
  const src = image?.fields?.image?.fields.file?.url;
  const url = cta?.fields?.url ?? image?.fields?.link;
  const newTab = cta?.fields?.openInNewTab === true || !!image?.fields?.link;
  const linkProps = newTab
    ? { target: "_blank", rel: "noopener noreferrer" }
    : {};

  const photo = src && (
    <Image
      src={src}
      alt={image?.fields.description ?? ""}
      fill
      sizes="(max-width: 768px) 100vw, 33vw"
      className="object-cover transition-transform duration-300 group-hover:scale-[1.03]"
      loader={customImageLoader}
    />
  );

  return (
    <div className={cn("h-full max-md:mx-auto", className)}>
      <ContentCard
        layout="media-top"
        tone={backgroundColor === "white" ? "white" : "none"}
        media={
          photo &&
          (url && !cta ? (
            <Link href={url} {...linkProps}>
              {photo}
            </Link>
          ) : (
            photo
          ))
        }
        title={heading}
        description={
          textBlock && (
            <ContentfulText content={textBlock} pClassName="mb-2 last:mb-0" />
          )
        }
        action={
          cta &&
          url && (
            <Button
              asChild
              variant="link"
              className={contentCardActionClassName}
            >
              <Link href={url} {...linkProps}>
                {cta.fields.buttonText}
                <FontAwesomeIcon icon={faArrowRight} />
              </Link>
            </Button>
          )
        }
      />
    </div>
  );
}
